import cloneDeep from 'lodash/cloneDeep';
import store from '@/store/store';
import { createAsync, updateAsync } from '@/api/devis';
import { ETAT_BROUILLON, REGROUPEMENT } from '@/configuration/devisConf';
import genererDevisCommande from '@/services/devis/genererDevisCommande';
import preparationEnvoiBackDevis from '@/services/devis/preparationEnvoiBack';
import calculCommandeDevis from '@/services/devisCommandes/calcul/calculCommandeDevis';
import { createOrUpdatePointDeVente } from '@/services/recommandation/clientDevisManager';
import createOrUpdateProduits, { panneauToProduitAsync } from '@/services/recommandation/produitDevisManager';
import { savePanneauxSousContrat } from '@/services/gestionPanneau/gestionPanneauSousContrat';
import ajoutLignesCommandeProduitFace from '@/services/devis/ajoutProduit/ajoutProduitFace';
import { gestionEcheanchier } from '@/services/devisCommandes/echeanciers/gestionEcheancier';

export function getAgenceId() {
  const societe = store.getters['societe/societeActive'];
  let agenceId = store.getters['user/agenceId'];
  if (societe.agences.some(a => a.id === agenceId)) return agenceId;
  return societe.agences[0].id;
}

export function genererDefautDevis() {
  const commercialId = store.getters['user/user'].id;
  const societe = store.getters['societe/societeActive'];
  return genererDevisCommande(societe, getAgenceId(), commercialId);
}

function deduplicateByGuid(array) {
  const seen = new Set();
  return array.filter(item => {
    if (seen.has(item.guid)) return false;
    seen.add(item.guid);
    return true;
  });
}

export async function panneauToLignesCommande(panneau, lignesCommande) {
  const produit = await panneauToProduitAsync(panneau);

  const maxOrdre = lignesCommande.reduce((max, lc) => Math.max(max, lc.ordre || 0), 0);
  let indexNouvel = 0;

  const lignes = [];
  panneau.budgets.forEach(budget => {
    const ligneOld = lignesCommande.find(lc =>
      (lc.id && lc.id === budget.ligneCommandeId)
      || lc.guid === budget.guid) || null;
    const ligneHorsMediaOld = lignesCommande.find(lc =>
      (lc.id && lc.id === budget.ligneCommandeIdHorsMedia)
      || (budget.guidHorsMedia && lc.guid === budget.guidHorsMedia)) || null;
    const ordre = ligneOld ? ligneOld.ordre : maxOrdre + (++indexNouvel * 100);

    lignes.push(...ajoutLignesCommandeProduitFace({
      panneau, produit, ordre,
      budget, ligneCommandeOld: ligneOld, ligneCommandeHorsMediaOld: ligneHorsMediaOld,
    }));
  });

  return lignes;
}

export default async function createOrUpdateRecommandationDevis(devisSource, panneauxSousContrat) {
  const devis = cloneDeep(devisSource);
  const intervenantAnnonceur = devis.intervenants.find(i => i.annonceur);
  const pointDeVente = await createOrUpdatePointDeVente(intervenantAnnonceur.tiers);
  const societe = store.getters['societe/societeActive'];

  devis.intervenants.forEach(i => {
    if (i.annonceur) {
      i.tiers = pointDeVente;
      i.tiersId = pointDeVente.id;
    }
  });

  const lignesRegroupement = devis.lignesCommande.filter(lc => lc.type === REGROUPEMENT);
  const produitsUniques = deduplicateByGuid(lignesRegroupement.map(lc => lc.produit));
  const produitsUpdated = await createOrUpdateProduits(produitsUniques);

  devis.lignesCommande.forEach(lc => {
    const updated = produitsUpdated.find(p => p.guid === lc.produit.guid);
    if (updated) {
      lc.produitId = updated.id;
      lc.produit = { ...lc.produit, ...updated };
      lc.libelle = lc.libelle || updated.libelle;

      if (societe) {
        const ligneFraisTechnique = lc.lignesCommande.find(l => l.produitId === societe.produitIdDefautFraisTechnique);
        if (ligneFraisTechnique) {
          const libelleBase = (societe.produitFraisTechnique && societe.produitFraisTechnique.libelle) || '';
          // ajout du nom du confrère sur la ligne de frais technique
          const nomConfrere = lc.fournisseur && lc.fournisseur.nomCommercial;
          ligneFraisTechnique.libelle = [libelleBase, updated.libelle, nomConfrere].filter(Boolean).join(' - ');
        }
      }
    }
  });

  devis.etat = ETAT_BROUILLON;
  calculCommandeDevis(devis);
  gestionEcheanchier(devis);

  const devisPayload = preparationEnvoiBackDevis(devis);
  const funcAsync = devis.id ? updateAsync : createAsync;
  const devisResult = (await funcAsync(devisPayload)).data.result;
  await savePanneauxSousContrat(panneauxSousContrat, devisResult.id);
  return devisResult;
}
